import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';

import { GTM_DATA_LAYER_NAME } from '../config/runtimeConfig';
import { useAnalytics } from '../providers/AnalyticsProvider';
import {
  useGetMyConsentQuery,
  usePostMyConsentMutation,
} from '../redux/consentApi';
import { useAuth } from '../redux/hooks';
import {
  ConsentState,
  POLICY_VERSION,
  clearConsent,
  readConsent,
  readConsentRecord,
  writeConsent,
} from '../utils/consentCookie';

const DATA_LAYER_NAME = GTM_DATA_LAYER_NAME;

export type { ConsentState };

interface ConsentContextValue {
  consent: ConsentState;
  accept: () => void;
  decline: () => void;
  resetConsent: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

const pushConsentUpdate = (granted: boolean) => {
  const state = granted ? 'granted' : 'denied';
  TagManager.dataLayer({
    dataLayer: {
      event: 'consent_update',
      consent: {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
      },
    },
    dataLayerName: DATA_LAYER_NAME,
  });
};

const ConsentProvider = (props: { children: ReactNode }) => {
  const { children } = props;
  const { user } = useAuth();
  const analytics = useAnalytics();
  const [consent, setConsent] = useState<ConsentState>(readConsent);

  const applyConsent = useCallback(
    (granted: boolean) => {
      pushConsentUpdate(granted);
      analytics.setConsent(granted);
    },
    [analytics]
  );

  const { data: serverRecord, isSuccess: serverFetched } = useGetMyConsentQuery(
    undefined,
    { skip: !user?.id }
  );
  const [postConsent] = usePostMyConsentMutation();

  const reconciledForRef = useRef<string | null>(null);

  // Newest-wins reconciliation between device cookie and server record.
  // When only the server has a record we adopt it; when only the cookie
  // does we leave it, so a shared device doesn't hoist an anonymous
  // decision onto the logged-in user's account.
  useEffect(() => {
    const userId = user?.id;
    if (!userId || !serverFetched) return;
    const reconciliationKey = `${userId}:${serverRecord?.id ?? 'none'}`;
    if (reconciledForRef.current === reconciliationKey) return;
    reconciledForRef.current = reconciliationKey;

    const local = readConsentRecord();

    if (serverRecord && local) {
      const serverTs = new Date(serverRecord.decidedAt).getTime();
      const localTs = local.decidedAt.getTime();
      if (serverTs > localTs) {
        writeConsent(serverRecord.decision, new Date(serverTs));
        setConsent(serverRecord.decision);
        applyConsent(serverRecord.decision === 'accepted');
      } else if (localTs > serverTs) {
        postConsent({
          decision: local.decision,
          policyVersion: POLICY_VERSION,
          decidedAt: new Date(localTs).toISOString(),
          source: 'api',
        });
      }
    } else if (serverRecord && !local) {
      writeConsent(serverRecord.decision, new Date(serverRecord.decidedAt));
      setConsent(serverRecord.decision);
      applyConsent(serverRecord.decision === 'accepted');
    }
  }, [user?.id, serverFetched, serverRecord, postConsent, applyConsent]);

  const previousUserIdRef = useRef<string | undefined>(user?.id);
  useEffect(() => {
    if (previousUserIdRef.current !== user?.id) {
      previousUserIdRef.current = user?.id;
      reconciledForRef.current = null;
    }
  }, [user?.id]);

  const recordDecision = useCallback(
    (decision: 'accepted' | 'declined') => {
      const now = new Date();
      writeConsent(decision, now);
      setConsent(decision);
      applyConsent(decision === 'accepted');
      if (user?.id) {
        postConsent({
          decision,
          policyVersion: POLICY_VERSION,
          decidedAt: now.toISOString(),
          source: 'banner',
        });
      }
    },
    [postConsent, user?.id, applyConsent]
  );

  const accept = useCallback(
    () => recordDecision('accepted'),
    [recordDecision]
  );
  const decline = useCallback(
    () => recordDecision('declined'),
    [recordDecision]
  );

  const resetConsent = useCallback(() => {
    clearConsent();
    setConsent(null);
    applyConsent(false);
  }, [applyConsent]);

  return (
    <ConsentContext.Provider value={{ consent, accept, decline, resetConsent }}>
      {children}
    </ConsentContext.Provider>
  );
};

const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
};

export { ConsentProvider, useConsent, ConsentContext };
